import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import Calendar from 'components/shared/Calendar'
import {
    ModalHeader,
    ModalFooter,
} from 'components/global/CalendarModal/styles'
import { formatDates } from 'helpers/dates'
import { useResolutions } from 'store/resolutions'
import { FormattedMessage } from 'react-intl'
import moment, { Moment } from 'moment'
import { useEffect, useState } from 'react'
import { closeModalActions } from 'helpers/helpers'
import { GetCalendarPresenter } from 'store/api-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import { CalendarLoader } from 'components/shared/Calendar/components/CalendarLoader'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import CalendarLegendItem from 'components/global/CalendarModal/components/CalendarModalLegend/components/CalendarLegendItem'

interface CabanasCalendarProps {
    isOpened: boolean
    close: () => void
    dateHandler: (value: string) => void
    initialDate: string
    errorHandler: (isError: boolean) => void
    data?: GetCalendarPresenter
    isFetching: boolean
    isError: boolean
}

const CabanasCalendarModal = ({
    isOpened,
    close,
    dateHandler,
    initialDate,
    errorHandler,
    data,
    isFetching,
    isError,
}: CabanasCalendarProps) => {
    const { isStepEmpty } = useCart()
    const { isDarkThemeForCabanas } = usePromotions()

    const [isDisabledCalendar, setIsDisabledCalendar] = useState(true)
    const [isConfirmed, setIsConfirmed] = useState(false)
    const [unconfirmedDates, setUnconfirmedDates] = useState<string[]>(
        initialDate === '' ? [] : [initialDate]
    )
    const [dates, setDates] = useState<string[]>([])
    const { isMobile } = useResolutions()

    const handleSetDates = (dates: Moment[]) => {
        setUnconfirmedDates([dates[0].format('YYYY-MM-DD')])
        if (!isMobile) {
            setIsConfirmed(true)
            dateHandler(dates[0].format('YYYY-MM-DD'))
        }
    }

    const confirmDates = () => {
        dateHandler(unconfirmedDates[0])
        setDates(unconfirmedDates)
        const closeModal = () => {
            setIsDisabledCalendar(false)
            setIsConfirmed(false)
            close()
            clearTimeout(timeout)
            closeModalActions(false)
        }

        const timeout = setTimeout(closeModal, 500)
    }

    useEffect(() => {
        if (isConfirmed) {
            setIsDisabledCalendar(true)
            confirmDates()
        }
    }, [isConfirmed])

    useEffect(() => {
        errorHandler(isError)
        if (isFetching || isError || !data) return
        setIsDisabledCalendar(false)
    }, [isFetching, isError])

    useEffect(() => {
        if (initialDate) setDates([])
    }, [initialDate])

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={786}
            onClose={close}
            isMobileFullscreen={true}
            testIdPrefix="cabana-calendar"
        >
            <>
                <ModalHeader hasDarkMode={isDarkThemeForCabanas} isFullWidth>
                    <Text
                        variant="body"
                        mb={4}
                        color={
                            isDarkThemeForCabanas
                                ? 'grayscale_500'
                                : 'grayscale_900'
                        }
                    >
                        <FormattedMessage
                            id="global.calendar.reservation_label"
                            defaultMessage="Termin rezerwacji"
                        />
                    </Text>

                    {dates.length || initialDate !== '' ? (
                        <Box direction="row" gap={4}>
                            <Text
                                variant="bodyLarge"
                                color={
                                    isDarkThemeForCabanas
                                        ? 'grayscale_300'
                                        : 'secondary_500'
                                }
                            >
                                {formatDates(
                                    dates.length > 0 ? dates : [initialDate]
                                )}
                            </Text>
                        </Box>
                    ) : (
                        <Text
                            variant="bodyLarge"
                            color={
                                isDarkThemeForCabanas
                                    ? 'grayscale_300'
                                    : 'secondary_500'
                            }
                        >
                            <FormattedMessage
                                id="global.calendar.reservation_placeholder"
                                defaultMessage="Wybierz termin rezerwacji"
                            />
                        </Text>
                    )}
                </ModalHeader>
                {isFetching ? (
                    <CalendarLoader />
                ) : (
                    <>
                        <Box>
                            <Calendar
                                mode="buses"
                                handleDatesChosen={handleSetDates}
                                datesInfo={data ? data : null}
                                isLimitedRange={true}
                                isDisabled={isDisabledCalendar}
                                date={
                                    initialDate === ''
                                        ? null
                                        : moment(initialDate)
                                }
                                testIdPrefix="cabana"
                                hasDarkMode={isDarkThemeForCabanas}
                            />
                        </Box>
                        <ModalFooter
                            gap={16}
                            align="flex-start"
                            hasDarkMode={isDarkThemeForCabanas}
                            isFullWidth
                        >
                            {(!isStepEmpty('tickets') ||
                                !isStepEmpty('accommodation')) && (
                                <CalendarLegendItem
                                    badge="highlight"
                                    description={
                                        <>
                                            {!isStepEmpty('tickets') &&
                                                isStepEmpty(
                                                    'accommodation'
                                                ) && (
                                                    <FormattedMessage
                                                        id="global.calendar.legend_staying_days"
                                                        defaultMessage="Dni Twojego pobytu w parku"
                                                    />
                                                )}
                                            {!isStepEmpty('accommodation') && (
                                                <FormattedMessage
                                                    id="global.calendar.legend_village_staying_days"
                                                    defaultMessage="Dni Twojego pobytu w Suntago Village"
                                                />
                                            )}
                                        </>
                                    }
                                />
                            )}
                            {isMobile && (
                                <Button
                                    onClick={confirmDates}
                                    isDisabled={unconfirmedDates.length === 0}
                                >
                                    <FormattedMessage
                                        id="global.btn.confirm"
                                        defaultMessage="Potwierdź"
                                    />
                                </Button>
                            )}
                        </ModalFooter>
                    </>
                )}
            </>
        </Modal>
    )
}

export default CabanasCalendarModal
